/**
 * Tenant scoping — the single choke point for multi-tenant safety.
 *
 * The headless API path connects with the service-role key, which bypasses RLS.
 * That makes the application layer the enforcer, so this module exists to make
 * an *unscoped* query impossible to express: every repository function takes a
 * `TenantScope` as its first argument, and builders route through
 * `scoped()`, which applies `.eq('account_id', ...)` for you.
 *
 * See `docs/authorization.md` for how this pairs with flat claim-RLS on the
 * admin path.
 */
/** Branded so a bare string can't be passed where a verified scope is required. */
export type AccountId = string & { readonly __brand: 'AccountId' };

export type TenantSource = 'api-key' | 'staff-jwt' | 'host';

export interface TenantScope {
  readonly accountId: AccountId;
  /** How this scope was established — useful for audit logs and debugging. */
  readonly source: TenantSource;
  /** Scopes granted to the API key, empty for staff-JWT scopes. */
  readonly scopes: readonly string[];
}

/**
 * Build a scope. Only call this from a place that has *verified* tenancy:
 * the API-key middleware (KV lookup) or a staff JWT claim check.
 */
export function createTenantScope(
  accountId: string,
  source: TenantSource,
  scopes: readonly string[] = [],
): TenantScope {
  if (!accountId) {
    throw new Error('createTenantScope: accountId is required');
  }

  return Object.freeze({
    accountId: accountId as AccountId,
    source,
    scopes: Object.freeze([...scopes]),
  });
}

export class TenantScopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TenantScopeError';
  }
}

/**
 * Apply the tenant predicate to a PostgREST query.
 *
 * Every read/write in the repository layer goes through here, so tenant
 * isolation is one reviewed line rather than a convention repeated at
 * ~140 call sites.
 */
export function scoped<T extends FilterableQuery>(query: T, scope: TenantScope): T {
  if (!scope?.accountId) {
    throw new TenantScopeError('A TenantScope with an accountId is required for every query');
  }

  return query.eq('account_id', scope.accountId) as T;
}

/**
 * Structural type for a PostgREST builder.
 *
 * Declared here rather than imported from `@supabase/postgrest-js` so this
 * module stays decoupled from that package's internal generics, which vary by
 * whether the table is in the generated Database type.
 */
interface FilterableQuery {
  eq(column: string, value: unknown): unknown;
}

/** Guard a route/handler on an API-key scope (e.g. `products:write`). */
export function assertScope(scope: TenantScope, required: string): void {
  // Staff-JWT scopes are authorized by permission claims, not API-key scopes.
  if (scope.source === 'staff-jwt') return;

  if (!scope.scopes.includes(required)) {
    throw new TenantScopeError(`Missing required scope: ${required}`);
  }
}
